import {
  Box,
  IconButton,
  Badge,
  useTheme,
  useMediaQuery,
  Popover,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { ColorModeContext } from './theme/theme';
import { Link, NavLink } from 'react-router-dom';
import { API_PATCH, DBRequest, useConfig } from '../api/api';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Notifications from './Notifications';
import Cookies from 'universal-cookie';
import { hasPermissions } from '../auth/hasPermissions';
import { ExoSmartSearch } from '../components/exo/ExoSmartSearch';
import { tokens } from './theme/tokens';
import { TimeBadge } from './TimeBadge';
import ExoAvatar from '../components/exo/ExoAvatar';
import { LanguageSwitcher } from '../components/special/LanguageSwitcher';
import { TopbarNavigation } from './TopbarNavigation';
import { useChatClient } from '../components/chat-system/useChatClient';
import { useTranslation } from 'react-i18next';

const Item = ({ to, icon }) => {
  return (
    <Link to={to}>
      <IconButton>{icon}</IconButton>
    </Link>
  );
};

const Topbar = ({ noAccountSettings = true }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const colorMode = useContext(ColorModeContext);
  const cookies = new Cookies();
  const config = useConfig();
  const { unreadTotal } = useChatClient();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);

  const firstName = cookies.get('firstName');
  const lastName = cookies.get('lastName');
  const profilePicture = cookies.get('profilePicture');
  const color = cookies.get('color');

  useEffect(() => {
    if (cookies.get('colorMode') === theme.palette.mode) return;

    cookies.set('colorMode', theme.palette.mode, {
      path: '/',
    });
    DBRequest({
      config,
      path: 'userpreferences/own',
      method: API_PATCH({ colorMode: theme.palette.mode }),
    });
  }, [theme.palette.mode]);

  const handleOpenMoreMenu = (event) => {
    setMoreMenuAnchor(event.currentTarget);
  };

  const handleCloseMoreMenu = () => {
    setMoreMenuAnchor(null);
  };

  return (
    <Box
      className="sticky md:absolute top-0 md:right-4 flex flex-row items-center backdrop-blur-md z-50 px-2 sm:px-3 rounded-xl gap-2 sm:gap-6 shadow-lg"
      display="flex"
      justifyContent="space-between"
      sx={{
        minHeight: isMobile ? '44px' : '50px',
        backgroundColor: colors.glass,
      }}
    >
      <Box className="flex gap-1 sm:gap-2 flex-1 min-w-0">
        {!isMobile && <TopbarNavigation />}
        <ExoSmartSearch />
        <Box className="hidden sm:block">
          <TimeBadge />
        </Box>
      </Box>

      <Box className="flex items-center py-1 flex-shrink-0">
        {!isMobile && (
          <IconButton onClick={colorMode.toggleColorMode} size="small">
            {theme.palette.mode === 'dark' ? (
              <LightModeOutlinedIcon />
            ) : (
              <DarkModeOutlinedIcon />
            )}
          </IconButton>
        )}
        {!isMobile && <LanguageSwitcher />}
        {hasPermissions('chat-message') && (
          <Link to="/chat">
            <IconButton size={isMobile ? 'small' : 'medium'}>
              <Badge
                badgeContent={unreadTotal}
                color="error"
                max={99}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.65rem',
                    minWidth: '16px',
                    height: '16px',
                  },
                }}
              >
                <ChatOutlinedIcon fontSize={isMobile ? 'small' : 'medium'} />
              </Badge>
            </IconButton>
          </Link>
        )}
        <Notifications />
        {!isMobile && hasPermissions('settings') && (
          <Item to="/settings" icon={<SettingsOutlinedIcon />} />
        )}

        {/* Mobile: More menu with hidden options */}
        {isMobile && (
          <>
            <IconButton size="small" onClick={handleOpenMoreMenu}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Popover
              open={Boolean(moreMenuAnchor)}
              anchorEl={moreMenuAnchor}
              onClose={handleCloseMoreMenu}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  backgroundColor: colors.glass,
                  backdropFilter: 'blur(10px)',
                  borderRadius: '12px',
                  p: 1,
                  minWidth: 180,
                },
              }}
            >
              <Box className="flex flex-col gap-1">
                {/* Dark Mode Toggle */}
                <Box
                  onClick={() => {
                    colorMode.toggleColorMode();
                    handleCloseMoreMenu();
                  }}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer"
                  sx={{ '&:hover': { backgroundColor: colors.card } }}
                >
                  {theme.palette.mode === 'dark' ? (
                    <LightModeOutlinedIcon fontSize="small" />
                  ) : (
                    <DarkModeOutlinedIcon fontSize="small" />
                  )}
                  <Typography variant="body2">
                    {theme.palette.mode === 'dark'
                      ? t('Light Mode')
                      : t('Dark Mode')}
                  </Typography>
                </Box>

                {/* Language Switcher */}
                <Box
                  className="flex items-center gap-3 px-3 py-2 rounded-lg"
                  sx={{ '&:hover': { backgroundColor: colors.card } }}
                >
                  <LanguageSwitcher />
                </Box>

                {/* Settings */}
                {hasPermissions('settings') && (
                  <Link to="/settings" onClick={handleCloseMoreMenu}>
                    <Box
                      className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer"
                      sx={{ '&:hover': { backgroundColor: colors.card } }}
                    >
                      <SettingsOutlinedIcon fontSize="small" />
                      <Typography variant="body2">{t('Settings')}</Typography>
                    </Box>
                  </Link>
                )}
              </Box>
            </Popover>
          </>
        )}

        <NavLink to="/profile" className="block sm:hidden ml-2 h-10">
          <ExoAvatar
            size="40px"
            id="nav-profile-picture"
            className="rounded-full mb-1 mx-auto"
            picture={profilePicture}
            name={firstName + ' ' + lastName}
            color={color}
          />
        </NavLink>
      </Box>
    </Box>
  );
};

export default Topbar;
